import React, { useState, useEffect, useCallback } from "react";
import {
  IconSearch,
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconFilter,
  IconRefresh,
  IconEye,
  IconPencil,
  IconTrash,
  IconCheck,
  IconClock,
  IconCalendar,
  IconUsers,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import { fetchHrEmployees, fetchHrDepartments } from "../../../apis/hrApi";

// ─── Types ──────────────────────────────────────────────────────────────────
type CompStatus = "Pending" | "Approved" | "Rejected";
type CompType = "Urgent" | "Applied";

interface CompensatoryItem {
  id: string;
  employee: { name: string; dept: string; avatar: string };
  compDate: string;
  type: CompType;
  hoursWorked: string;
  shiftHours: string;
  processedBy: { name: string; dept: string; avatar: string } | null;
  approval: CompStatus | null;
  status: CompStatus;
  createdAt: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function avatar(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const TYPE_BADGE: Record<CompType, string> = {
  Urgent: "bg-red-50 text-red-600 border-red-100",
  Applied: "bg-blue-50 text-blue-600 border-blue-100",
};

function StatusBadge({
  status,
  onChange,
}: {
  status: CompStatus;
  onChange?: (s: CompStatus) => void;
}) {
  const [open, setOpen] = useState(false);

  const colors: Record<CompStatus, string> = {
    Approved: "bg-green-50 text-green-700 border-green-200",
    Rejected: "bg-red-50 text-red-600 border-red-200",
    Pending: "bg-yellow-50 text-yellow-600 border-yellow-200",
  };
  const dots: Record<CompStatus, string> = {
    Approved: "bg-green-500",
    Rejected: "bg-red-500",
    Pending: "bg-yellow-400",
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (onChange) setOpen((v) => !v);
        }}
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl border text-[11px] font-bold transition shadow-sm ${colors[status]} ${onChange ? "cursor-pointer hover:opacity-80" : ""}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dots[status]}`} />
        {status}
        {onChange && (
          <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </button>
      {open && onChange && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute z-30 mt-1 w-32 bg-white rounded-xl border border-gray-200 shadow-xl py-1 left-0">
            {(["Pending", "Approved", "Rejected"] as CompStatus[]).map((s) => (
              <button
                key={s}
                onClick={() => {
                  onChange(s);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-gray-50 font-semibold text-gray-700 flex items-center gap-1.5 cursor-pointer"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${dots[s]}`} />
                {s}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Mock / Seed Data ────────────────────────────────────────────────────────
const SEED_DATA: CompensatoryItem[] = [
  {
    id: "1",
    employee: { name: "Pal", dept: "Software Development", avatar: "" },
    compDate: "07-07-2026",
    type: "Urgent",
    hoursWorked: "07h 16m",
    shiftHours: "10:00 AM – 05:30 PM",
    processedBy: { name: "Pal", dept: "Software Development", avatar: "" },
    approval: null,
    status: "Approved",
    createdAt: "07-07-2026 11:18 AM",
  },
  {
    id: "2",
    employee: { name: "Emo", dept: "Human Resources", avatar: "" },
    compDate: "04-07-2026",
    type: "Urgent",
    hoursWorked: "09h 08m",
    shiftHours: "10:00 AM – 05:30 PM",
    processedBy: { name: "Emo", dept: "Human Resources", avatar: "" },
    approval: null,
    status: "Approved",
    createdAt: "04-07-2026 09:37 AM",
  },
  {
    id: "3",
    employee: { name: "Marshal Man", dept: "—", avatar: "" },
    compDate: "04-07-2026",
    type: "Applied",
    hoursWorked: "—",
    shiftHours: "10:00 AM – 05:30 PM",
    processedBy: null,
    approval: "Approved",
    status: "Pending",
    createdAt: "29-06-2026 05:59 PM",
  },
  {
    id: "4",
    employee: { name: "Riva", dept: "Human Resources", avatar: "" },
    compDate: "06-06-2026",
    type: "Applied",
    hoursWorked: "—",
    shiftHours: "10:00 AM – 05:30 PM",
    processedBy: { name: "Yash Koladiya", dept: "Human Resources", avatar: "" },
    approval: "Approved",
    status: "Pending",
    createdAt: "27-06-2026 06:22 PM",
  },
  {
    id: "5",
    employee: { name: "Marshal Man", dept: "—", avatar: "" },
    compDate: "06-06-2026",
    type: "Applied",
    hoursWorked: "—",
    shiftHours: "10:00 AM – 05:30 PM",
    processedBy: null,
    approval: "Approved",
    status: "Pending",
    createdAt: "27-06-2026 06:20 PM",
  },
];

// ─── Main Component ──────────────────────────────────────────────────────────
export default function CompensatoryRequest() {
  const [data, setData] = useState<CompensatoryItem[]>(SEED_DATA);
  const [isLoading, setIsLoading] = useState(false);

  // Filter states
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesLimit] = useState(10);

  // Sidebar
  type SideMode = "ADD" | "VIEW" | "EDIT" | "DELETE" | "FILTER" | null;
  const [sidebarMode, setSidebarMode] = useState<SideMode>(null);
  const [selected, setSelected] = useState<CompensatoryItem | null>(null);

  // Form state for Add/Edit
  const [formEmployee, setFormEmployee] = useState("");
  const [formCompDate, setFormCompDate] = useState("");
  const [formType, setFormType] = useState<CompType>("Applied");
  const [formHoursWorked, setFormHoursWorked] = useState("");
  const [formShiftStart, setFormShiftStart] = useState("10:00");
  const [formShiftEnd, setFormShiftEnd] = useState("17:30");
  const [formReason, setFormReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropdown data
  const [employeesList, setEmployeesList] = useState<any[]>([]);
  const [departmentsList, setDepartmentsList] = useState<any[]>([]);

  // Load employees & departments
  const loadMeta = useCallback(async () => {
    try {
      const [empRes, deptRes] = await Promise.all([
        fetchHrEmployees(1, 1000),
        fetchHrDepartments(1, 100),
      ]);
      if (empRes.success && empRes.data?.employees) setEmployeesList(empRes.data.employees);
      if (deptRes.success && deptRes.data?.departments) setDepartmentsList(deptRes.data.departments);
    } catch (e) {
      console.error("Failed to load meta data", e);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  // Derived — filtered + search
  const displayed = data.filter((item) => {
    const q = searchQuery.toLowerCase();
    if (q && !item.employee.name.toLowerCase().includes(q) && !item.employee.dept.toLowerCase().includes(q)) return false;
    if (filterStatus && item.status !== filterStatus) return false;
    if (filterType && item.type !== filterType) return false;
    if (filterDept && !item.employee.dept.toLowerCase().includes(filterDept.toLowerCase())) return false;
    if (filterEmployee && !item.employee.name.toLowerCase().includes(filterEmployee.toLowerCase())) return false;
    return true;
  });

  // Check active filters
  useEffect(() => {
    setHasActiveFilters(
      !!(filterEmployee || filterDept || filterStatus || filterType || filterDateFrom || filterDateTo)
    );
  }, [filterEmployee, filterDept, filterStatus, filterType, filterDateFrom, filterDateTo]);

  const clearFilters = () => {
    setFilterEmployee("");
    setFilterDept("");
    setFilterStatus("");
    setFilterType("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const paginated = displayed.slice((currentPage - 1) * entriesLimit, currentPage * entriesLimit);
  const totalPages = Math.ceil(displayed.length / entriesLimit);

  const handleStatusChange = (id: string, newStatus: CompStatus) => {
    setData((prev) => prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item)));
    if (selected?.id === id) setSelected((s) => (s ? { ...s, status: newStatus } : s));
    toast.success(`Status updated to ${newStatus}`);
  };

  const handleApprovalChange = (id: string, newApproval: CompStatus) => {
    setData((prev) => prev.map((item) => (item.id === id ? { ...item, approval: newApproval } : item)));
    toast.success(`Approval updated to ${newApproval}`);
  };

  const resetForm = () => {
    setFormEmployee("");
    setFormCompDate("");
    setFormType("Applied");
    setFormHoursWorked("");
    setFormShiftStart("10:00");
    setFormShiftEnd("17:30");
    setFormReason("");
  };

  const handleOpenAdd = () => {
    resetForm();
    setSidebarMode("ADD");
  };

  const handleOpenEdit = (item: CompensatoryItem) => {
    setSelected(item);
    setFormEmployee(item.employee.name);
    setFormCompDate(item.compDate);
    setFormType(item.type);
    setFormHoursWorked(item.hoursWorked);
    setFormShiftStart("10:00");
    setFormShiftEnd("17:30");
    setFormReason("");
    setSidebarMode("EDIT");
  };

  const handleSubmit = async () => {
    if (!formEmployee.trim()) { toast.error("Please enter employee name"); return; }
    if (!formCompDate) { toast.error("Please select compensatory date"); return; }
    setIsSubmitting(true);
    try {
      await new Promise((r) => setTimeout(r, 800));
      if (sidebarMode === "ADD") {
        const newItem: CompensatoryItem = {
          id: String(Date.now()),
          employee: { name: formEmployee, dept: "—", avatar: "" },
          compDate: formCompDate,
          type: formType,
          hoursWorked: formHoursWorked || "—",
          shiftHours: `${formShiftStart} – ${formShiftEnd}`,
          processedBy: null,
          approval: null,
          status: "Pending",
          createdAt: new Date().toLocaleString(),
        };
        setData((prev) => [newItem, ...prev]);
        toast.success("Compensatory request created!");
      } else if (sidebarMode === "EDIT" && selected) {
        setData((prev) =>
          prev.map((item) =>
            item.id === selected.id
              ? { ...item, employee: { ...item.employee, name: formEmployee }, compDate: formCompDate, type: formType }
              : item
          )
        );
        toast.success("Compensatory request updated!");
      }
      setSidebarMode(null);
      setSelected(null);
      resetForm();
    } catch {
      toast.error("Failed to save request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    setData((prev) => prev.filter((item) => item.id !== selected.id));
    toast.success("Request deleted.");
    setSidebarMode(null);
    setSelected(null);
  };

  const isSidebarOpen = sidebarMode !== null;

  // ─── Sidebar Content ───────────────────────────────────────────────────────
  const renderSidebar = () => {
    if (!sidebarMode) return null;

    if (sidebarMode === "FILTER") {
      return (
        <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden h-[calc(100vh-170px)] sticky top-[95px]">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E9E9E9] bg-[#F7F6F2]">
            <div className="flex items-center gap-2">
              <IconFilter size={18} className="text-[#C9A100]" />
              <h2 className="text-[#212121] text-xs font-bold uppercase tracking-wide">Filter Requests</h2>
            </div>
            <button onClick={() => setSidebarMode(null)} className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-50 text-slate-500 cursor-pointer">
              <IconX size={16} />
            </button>
          </div>
          <div className="p-5 space-y-4 overflow-auto flex-1">
            {/* Employee */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Employee</label>
              <input
                type="text"
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                placeholder="Search employee..."
                className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition"
              />
            </div>

            {/* Department */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Department</label>
              <select
                value={filterDept}
                onChange={(e) => setFilterDept(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition cursor-pointer"
              >
                <option value="">All Departments</option>
                {departmentsList.map((d) => (
                  <option key={d.id || d._id} value={d.departmentName || d.name}>
                    {d.departmentName || d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition cursor-pointer"
              >
                <option value="">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            {/* Type */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition cursor-pointer"
              >
                <option value="">All Types</option>
                <option value="Applied">Applied</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Date From</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Date To</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition"
              />
            </div>
          </div>

          <div className="flex gap-3 px-5 py-4 border-t border-gray-100 mt-auto bg-[#F7F6F2]">
            <button
              type="button"
              onClick={clearFilters}
              className="flex-1 px-4 py-2.5 border border-gray-200 bg-white rounded-xl text-xs font-bold text-gray-700 hover:bg-gray-50 transition cursor-pointer"
            >
              Clear Filters
            </button>
            <button
              type="button"
              onClick={() => setSidebarMode(null)}
              className="flex-1 px-5 py-2.5 rounded-xl text-xs font-bold shadow-sm transition cursor-pointer bg-[#FBD037] hover:bg-[#FACC15] text-[#212121]"
            >
              Apply
            </button>
          </div>
        </div>
      );
    }

    if (sidebarMode === "DELETE" && selected) {
      return (
        <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden p-6 space-y-5">
          <div className="flex items-center justify-between pb-3.5 border-b border-gray-100">
            <h2 className="text-base font-bold text-[#212121]">Delete Request</h2>
            <button onClick={() => { setSidebarMode(null); setSelected(null); }} className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-50 text-slate-500 cursor-pointer">
              <IconX size={16} />
            </button>
          </div>
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <IconTrash size={24} className="text-red-500" />
            </div>
            <p className="text-sm font-semibold text-[#212121]">Are you sure?</p>
            <p className="text-xs text-gray-500 mt-1">This will permanently delete the compensatory request for <strong>{selected.employee.name}</strong>.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => { setSidebarMode("VIEW"); }} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-gray-50 cursor-pointer">Cancel</button>
            <button onClick={handleDelete} className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs font-bold cursor-pointer">Delete</button>
          </div>
        </div>
      );
    }

    if (sidebarMode === "VIEW" && selected) {
      return (
        <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden p-6 space-y-5">
          <div className="flex items-center justify-between pb-3.5 border-b border-gray-100">
            <h2 className="text-base font-bold text-[#212121]">Request Details</h2>
            <button onClick={() => { setSidebarMode(null); setSelected(null); }} className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-50 text-slate-500 cursor-pointer">
              <IconX size={16} />
            </button>
          </div>

          {/* Employee card */}
          <div className="bg-slate-50 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#FBD037]/20 border border-[#FBD037]/40 flex items-center justify-center text-sm font-bold text-[#212121] flex-shrink-0">
              {avatar(selected.employee.name)}
            </div>
            <div>
              <p className="text-sm font-bold text-[#212121]">{selected.employee.name}</p>
              <p className="text-xs text-gray-500">{selected.employee.dept}</p>
            </div>
            <div className="ml-auto">
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${TYPE_BADGE[selected.type]}`}>
                {selected.type}
              </span>
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            {[
              { label: "Comp Date", value: selected.compDate },
              { label: "Hours Worked", value: selected.hoursWorked },
              { label: "Shift Hours", value: selected.shiftHours, full: true },
              { label: "Created At", value: selected.createdAt, full: true },
            ].map((row) => (
              <div key={row.label} className={row.full ? "col-span-2" : ""}>
                <span className="block text-[10px] font-bold text-gray-400 uppercase mb-0.5">{row.label}</span>
                <span className="font-semibold text-gray-700">{row.value}</span>
              </div>
            ))}
          </div>

          {/* Status section */}
          <div className="border border-gray-100 rounded-2xl p-4 space-y-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase">Status</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="block text-[10px] text-gray-400 mb-1">Approval</span>
                {selected.approval ? (
                  <StatusBadge status={selected.approval} onChange={(s) => handleApprovalChange(selected.id, s)} />
                ) : (
                  <span className="text-gray-300 font-semibold text-xs">—</span>
                )}
              </div>
              <div>
                <span className="block text-[10px] text-gray-400 mb-1">Request Status</span>
                <StatusBadge status={selected.status} onChange={(s) => handleStatusChange(selected.id, s)} />
              </div>
            </div>
          </div>

          {/* Processed By */}
          {selected.processedBy && (
            <div className="border border-gray-100 rounded-2xl p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Processed By</p>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                  {avatar(selected.processedBy.name)}
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#212121]">{selected.processedBy.name}</p>
                  <p className="text-[10px] text-gray-400">{selected.processedBy.dept}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {selected.status === "Pending" && (
            <div className="flex gap-3 pt-1">
              <button onClick={() => handleStatusChange(selected.id, "Approved")} className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold py-2.5 rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer">
                <IconCheck size={15} /> Approve
              </button>
              <button onClick={() => handleStatusChange(selected.id, "Rejected")} className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold py-2.5 rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer">
                <IconX size={15} /> Reject
              </button>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => handleOpenEdit(selected)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 border border-gray-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-gray-50 cursor-pointer">
              <IconPencil size={14} /> Edit
            </button>
            <button onClick={() => setSidebarMode("DELETE")} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 border border-red-100 rounded-xl text-xs font-semibold text-red-500 hover:bg-red-50 cursor-pointer">
              <IconTrash size={14} /> Delete
            </button>
          </div>
        </div>
      );
    }

    // ADD / EDIT form
    return (
      <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#212121]">
            {sidebarMode === "ADD" ? "New Compensatory Request" : "Edit Request"}
          </h2>
          <button onClick={() => { setSidebarMode(null); setSelected(null); resetForm(); }} className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-50 text-slate-500 cursor-pointer">
            <IconX size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-auto">
          {/* Type */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Request Type</label>
            <div className="flex gap-3">
              {(["Applied", "Urgent"] as CompType[]).map((t) => (
                <label key={t} className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 cursor-pointer">
                  <input type="radio" name="compType" checked={formType === t} onChange={() => setFormType(t)} className="w-3.5 h-3.5 accent-[#FBD037]" />
                  {t}
                </label>
              ))}
            </div>
          </div>

          {/* Employee */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Employee *</label>
            {employeesList.length > 0 ? (
              <select
                value={formEmployee}
                onChange={(e) => setFormEmployee(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition"
              >
                <option value="">Select Employee</option>
                {employeesList.map((emp) => (
                  <option key={emp.id || emp._id} value={`${emp.firstName} ${emp.lastName}`.trim()}>
                    {`${emp.firstName} ${emp.lastName}`.trim()}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={formEmployee}
                onChange={(e) => setFormEmployee(e.target.value)}
                placeholder="Employee name"
                className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition"
              />
            )}
          </div>

          {/* Compensatory Date */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Compensatory Date *</label>
            <input
              type="date"
              value={formCompDate}
              onChange={(e) => setFormCompDate(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition"
            />
          </div>

          {/* Hours Worked */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Hours Worked</label>
            <input
              type="text"
              value={formHoursWorked}
              onChange={(e) => setFormHoursWorked(e.target.value)}
              placeholder="e.g. 08h 30m"
              className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition"
            />
          </div>

          {/* Shift Hours */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Shift Hours</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-[10px] text-gray-400 mb-0.5 block">Start</span>
                <input
                  type="time"
                  value={formShiftStart}
                  onChange={(e) => setFormShiftStart(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition"
                />
              </div>
              <div>
                <span className="text-[10px] text-gray-400 mb-0.5 block">End</span>
                <input
                  type="time"
                  value={formShiftEnd}
                  onChange={(e) => setFormShiftEnd(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition"
                />
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Reason</label>
            <textarea
              value={formReason}
              onChange={(e) => setFormReason(e.target.value)}
              placeholder="Enter reason for compensatory request..."
              rows={3}
              className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 mt-auto">
          <button
            onClick={() => { setSidebarMode(null); setSelected(null); resetForm(); }}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-[#212121] hover:bg-slate-800 text-white rounded-xl text-xs font-bold cursor-pointer transition flex items-center justify-center gap-2"
          >
            {isSubmitting && (
              <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            {isSubmitting ? "Saving..." : sidebarMode === "ADD" ? "Create Request" : "Save Changes"}
          </button>
        </div>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full mx-auto mt-2 space-y-4 animate-in fade-in duration-300">
      <div className={`grid gap-6 w-full ${isSidebarOpen ? "grid-cols-12" : "grid-cols-12"}`}>
        {/* Main Table Card */}
        <div className={`${isSidebarOpen ? "col-span-12 xl:col-span-7" : "col-span-12"} flex flex-col transition-all duration-300`}>
          <div className="bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl rounded-3xl border border-white/20 shadow-sm overflow-hidden">
            {/* Toolbar */}
            <div className="px-5 py-3 flex items-center justify-between gap-3 border-b border-slate-50 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-bold text-[#212121]">
                <div className="w-7 h-7 rounded-lg bg-[#FBD037]/20 flex items-center justify-center">
                  <IconClock size={15} className="text-[#C9A100]" />
                </div>
                Compensatory Requests
                <span className="ml-1 text-[10px] font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                  {displayed.length}
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {/* Filter toggle */}
                <button
                  onClick={() => {
                    if (sidebarMode === "FILTER") {
                      setSidebarMode(null);
                    } else {
                      setSidebarMode("FILTER");
                      setSelected(null);
                    }
                  }}
                  className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border text-xs font-semibold transition shadow-sm cursor-pointer ${
                    sidebarMode === "FILTER" || hasActiveFilters
                      ? "border-[#FBD037] bg-[#FBD037]/10 text-[#212121]"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <IconFilter size={15} stroke={2} />
                  Filter
                  {hasActiveFilters && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A100] ml-0.5" />
                  )}
                </button>

                {/* Search */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => !showSearch && setShowSearch(true)}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-44 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50"
                    }`}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowSearch(!showSearch); if (showSearch) setSearchQuery(""); }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0 cursor-pointer"
                    >
                      <IconSearch size={15} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                        />
                        {searchQuery && (
                          <button onClick={(e) => { e.stopPropagation(); setSearchQuery(""); }} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                            <IconX size={13} stroke={2.5} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Refresh */}
                <button
                  onClick={() => { setSearchQuery(""); setShowSearch(false); clearFilters(); setShowFilterPanel(false); }}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition shadow-sm cursor-pointer"
                  title="Reset"
                >
                  <IconRefresh size={15} stroke={2.5} />
                </button>

                {/* Add */}
                <button
                  onClick={handleOpenAdd}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#FACC15] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                  title="Add Request"
                >
                  <IconPlus stroke={2.5} size={16} className="text-[#212121]" />
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-auto max-h-[70vh] hide-scrollbar">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-dashed border-gray-200 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-5 py-3 min-w-[180px]">Employee</th>
                    <th className="text-left px-4 py-3">Comp Date</th>
                    <th className="text-left px-4 py-3">Type</th>
                    {!isSidebarOpen && (
                      <>
                        <th className="text-left px-4 py-3">Hours Worked</th>
                        <th className="text-left px-4 py-3">Shift Hours</th>
                        <th className="text-left px-4 py-3">Processed By</th>
                        <th className="text-left px-4 py-3">Approval</th>
                      </>
                    )}
                    <th className="text-left px-4 py-3">Status</th>
                    {!isSidebarOpen && <th className="text-left px-4 py-3">Created At</th>}
                    <th className="text-center px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-transparent">
                  {isLoading ? (
                    <tr>
                      <td colSpan={10} className="text-center py-12">
                        <svg className="animate-spin h-6 w-6 text-[#FBD037] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                      </td>
                    </tr>
                  ) : paginated.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-12 text-gray-400 text-xs font-semibold">
                        No compensatory requests found
                      </td>
                    </tr>
                  ) : (
                    paginated.map((item) => (
                      <tr
                        key={item.id}
                        className={`hover:bg-[#FBD037]/10 group transition-colors cursor-pointer border-b border-dashed border-gray-100 ${
                          selected?.id === item.id && isSidebarOpen ? "bg-[#FBD037]/5" : ""
                        }`}
                        onClick={() => { setSelected(item); setSidebarMode("VIEW"); }}
                      >
                        {/* Employee */}
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-xl bg-[#FBD037]/20 border border-[#FBD037]/30 flex items-center justify-center text-xs font-bold text-[#212121] flex-shrink-0">
                              {avatar(item.employee.name)}
                            </div>
                            <div>
                              <div className="text-[13px] font-semibold text-[#212121]">{item.employee.name}</div>
                              <div className="text-[11px] text-gray-400">{item.employee.dept}</div>
                            </div>
                          </div>
                        </td>

                        {/* Comp Date */}
                        <td className="px-4 py-3 text-xs font-semibold text-gray-700">{item.compDate}</td>

                        {/* Type */}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${TYPE_BADGE[item.type]}`}>
                            {item.type}
                          </span>
                        </td>

                        {!isSidebarOpen && (
                          <>
                            {/* Hours Worked */}
                            <td className="px-4 py-3 text-xs font-semibold text-gray-700">{item.hoursWorked}</td>

                            {/* Shift Hours */}
                            <td className="px-4 py-3 text-xs text-gray-600">{item.shiftHours}</td>

                            {/* Processed By */}
                            <td className="px-4 py-3">
                              {item.processedBy ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600 flex-shrink-0">
                                    {avatar(item.processedBy.name)}
                                  </div>
                                  <div>
                                    <div className="text-[12px] font-semibold text-[#212121]">{item.processedBy.name}</div>
                                    <div className="text-[10px] text-gray-400">{item.processedBy.dept}</div>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-300 font-semibold">—</span>
                              )}
                            </td>

                            {/* Approval */}
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              {item.approval ? (
                                <StatusBadge status={item.approval} onChange={(s) => handleApprovalChange(item.id, s)} />
                              ) : (
                                <span className="text-gray-300 font-semibold text-xs">—</span>
                              )}
                            </td>
                          </>
                        )}

                        {/* Status */}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <StatusBadge status={item.status} onChange={(s) => handleStatusChange(item.id, s)} />
                        </td>

                        {!isSidebarOpen && (
                          <td className="px-4 py-3 text-xs text-gray-500">{item.createdAt}</td>
                        )}

                        {/* Actions */}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              onClick={() => { setSelected(item); setSidebarMode("VIEW"); }}
                              className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition text-slate-500 cursor-pointer bg-white"
                              title="View"
                            >
                              <IconEye size={13} stroke={1.5} />
                            </button>
                            <button
                              onClick={() => handleOpenEdit(item)}
                              className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition text-slate-500 cursor-pointer bg-white"
                              title="Edit"
                            >
                              <IconPencil size={13} stroke={1.5} />
                            </button>
                            <button
                              onClick={() => { setSelected(item); setSidebarMode("DELETE"); }}
                              className="w-7 h-7 rounded-full border border-red-100 flex items-center justify-center hover:bg-red-50 transition text-red-400 cursor-pointer bg-white"
                              title="Delete"
                            >
                              <IconTrash size={13} stroke={1.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="py-3.5 px-5 flex items-center justify-between border-t border-slate-50">
              <span className="text-[11px] text-gray-400 font-medium">
                {displayed.length === 0 ? "No records" : `Record ${(currentPage - 1) * entriesLimit + 1}–${Math.min(currentPage * entriesLimit, displayed.length)} of ${displayed.length}`}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded-md border text-gray-400 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                >
                  <IconChevronLeft size={18} />
                </button>
                <button className="px-3 py-1 rounded-md bg-[#FBD037] text-[#212121] font-bold text-xs shadow-sm">
                  {currentPage}
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="p-1 rounded-md border text-gray-400 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                >
                  <IconChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        {isSidebarOpen && (
          <div className="col-span-12 xl:col-span-5 animate-in fade-in slide-in-from-right-5 duration-300">
            {renderSidebar()}
          </div>
        )}
      </div>
    </div>
  );
}
